import { localDateInputValue } from '../utils/localDateTime';
import { showAppAlert } from '../common/appDialog';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { recordGeneratedReport } from '../utils/recordGeneratedReport';
import S02FacilityPlanWorkspace from './S02FacilityPlanWorkspace';
import S03StaffProtectionWorkspace from './S03StaffProtectionWorkspace';
import S16EmergencyPreparednessWorkspace from './S16EmergencyPreparednessWorkspace';
import S14BusinessResiliencyWorkspace from './S14BusinessResiliencyWorkspace';
import S15HSSEComplianceWorkspace from './S15HSSEComplianceWorkspace';
import S13CrisisManagementWorkspace from './S13CrisisManagementWorkspace';
import S04EventSecurityWorkspace from './S04EventSecurityWorkspace';
import IntelligenceServiceWorkspace from './IntelligenceServiceWorkspace';
import ServiceRecallWorkspace from './ServiceRecallWorkspace';
import { fullWorkspaceAccess } from '../common/organizationAccessMode';
import { buildExecutiveReportConfig, readExecutiveReportResponse } from '../utils/executiveReportSubmission';
import { useGlobalAssets } from '../context/GlobalAssetContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { SUITE_WORKFLOWS, SUITE_DETAILS, ICONS } from './constants';
import type { FormState, AnalysisOrPlanResult, GroundingChunk, Student, EnrolledCourse, CompetencyRecommendationResult, LessonPlan, EpProject, FrameworkComponent, EpThreatAnalysisResult } from '@types';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { SrmProject, SrmModule, Page, NavParams, ActiveFramework, UserProfile, Organization, CountryRiskData, GeopoliticalEvent, AcapsCrisis } from '@types';
import AnalysisForm from './AnalysisForm';
import AnalysisResultDisplay from './AnalysisResultDisplay';
import Loader from './Loader';
import ApiErrorDisplay from './ApiErrorDisplay';
import { WorkflowChecklist } from './WorkflowChecklist';
import VCMC from './VCMC';
import VCMCSetup from './VCMCSetup';
import IndustryCompetencyBuilder from './IndustryCompetencyBuilder';
import SavedReportsList from './SavedReportsList';
import IntegratedThreatAnalysis from './IntegratedThreatAnalysis';

// CMP Suite Imports
import { CrisisTemplateBuilder } from './CrisisTemplateBuilder';
import { CrisisManagementBuilder } from './CrisisManagementBuilder';
import { BusinessResiliencyBuilder } from './BusinessResiliencyBuilder';
import { EmergencyPreparednessBuilder } from './EmergencyPreparednessBuilder';
import { LocalIncidentManagementBuilder } from './LocalIncidentManagementBuilder';

import EpDashboard from './EpDashboard';
import EpOperationsDashboard from './EpOperationsDashboard';
import EpAdvanceWorkflow from './EpAdvanceWorkflow';
import EpSurveyRequestPicker from './EpSurveyRequestPicker';
import EpProjectHub, { EpProjectRequestPicker } from './EpProjectHub';
import TrmBriefHub from './TrmBriefHub';
import TrmBriefRequestPicker from './TrmBriefRequestPicker';
import S01AssessmentWorkspace from './S01AssessmentWorkspace';
import EpPreMovementBrief from './EpPreMovementBrief';
import { collection, getDocs, query, where, arrayUnion, addDoc } from 'firebase/firestore';
import { geocode } from '../../services/mapboxService';
import UnifiedMap from './UnifiedMap';
import { RiskMonitorDashboard } from './RiskMonitorDashboard';
import useGoogleMaps from './useGoogleMaps';
// Keep generateEpOperationOrder and generateEpTacticalRecommendations if they are still needed for other functionalities
const generateEpOperationOrder = async (data: any): Promise<string> => "Mock OPORD";
const generateEpTacticalRecommendations = async (data: any): Promise<{personnel: string, logistics: string}> => ({ personnel: "TBD", logistics: "TBD" });


interface SuiteDashboardProps {
    suiteName: string;
    riskData?: CountryRiskData[]; // <--- ADD THIS
    alerts?: (GeopoliticalEvent | AcapsCrisis)[]; // <--- UPDATED FOR MULTI-INT FEED
    initialWorkflowId?: string;
    navParams?: NavParams;
    onBack: () => void;
    onRequestHelp: (suiteName: string, workflowName?: string, preferChat?: boolean) => void;
    onOpenChatBot?: () => void;
    onNavigateToGtgDashboard: () => void;
    onNavigateToGsocDashboard: () => void;
    onNavigateToSuite: (suiteName: string) => void;
    onOpenAvToolsModal: () => void;
    onNavigate: (nav: { page: Page; params?: NavParams }) => void;
    onOpenGlossary?: (term: string) => void;
    students?: Student[];
    onCreateStudent?: (studentData: any) => Promise<Student>;
    onCreateCustomCourse?: (course: EnrolledCourse, studentId: string) => void;
    onUpdateStudentCourse?: (studentId: string, courseId: string, updates: Partial<EnrolledCourse>) => void;
    customLmsModules?: any[];
    onCreateCustomModule?: (newModule: any) => void;
    customCourseDefinitions?: any[];
    onCreateCourseDefinition?: (courseDefinition: { courseName: string; modules: string[] }) => void;
    onEnrollStudent?: (course: EnrolledCourse, studentId: string) => void;
    standardCourseDefinitions?: any[];
    lessonPlans?: LessonPlan[];
    onSaveLessonPlan?: (moduleId: string, title: string, content: string) => void;
    onUpdateSrmProjectModule?: (projectId: string, moduleId: string, resultData: AnalysisOrPlanResult) => Promise<void>;
    activeFramework?: ActiveFramework | null;
    onUpdateFrameworkComponent?: (componentId: string, updates: Partial<FrameworkComponent>) => Promise<void>;
    currentUser: UserProfile | null;
    currentOrganization: Organization | null;
    srmProject?: SrmProject | null;
    srmModule?: SrmModule | null;
}

interface WorkflowCardProps {
    workflow: { id: string; name: string; description: string; icon: React.ReactElement };
    onClick: () => void;
}

const WorkflowCard: React.FC<WorkflowCardProps> = ({ workflow, onClick }) => (
    <div 
        onClick={onClick}
        className="group cursor-pointer bg-white p-5 rounded-xl border border-slate-200 shadow-sm hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 flex flex-col"
    >
        <div className="flex items-center mb-3">
            <div className="p-2.5 rounded-lg bg-slate-100 text-blue-600 group-hover:bg-blue-100 transition-colors">
                 {React.cloneElement(workflow.icon as React.ReactElement<any>, { className: 'h-6 w-6' })}
            </div>
        </div>
        <h3 className="text-lg font-bold text-slate-800 mb-1">{workflow.name}</h3>
        <p className="text-sm text-slate-600 flex-grow">{workflow.description}</p>
        <div className="text-right mt-4 h-5">
            <span className="text-sm font-semibold text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">
                Start &rarr;
            </span>
        </div>
    </div>
);

const ComingSoonWorkflowCard: React.FC<{ workflow: { name: string; description: string; icon: React.ReactElement } }> = ({ workflow }) => (
    <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-col">
        <div className="flex items-center mb-3">
            <div className="p-2.5 rounded-lg bg-slate-100 text-slate-500">
                {React.cloneElement(workflow.icon as React.ReactElement<any>, { className: 'h-6 w-6' })}
            </div>
        </div>
        <h3 className="text-lg font-bold text-slate-800 mb-1">{workflow.name}</h3>
        <p className="text-sm text-slate-600 flex-grow">{workflow.description}</p>
        <span className="mt-4 self-start rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-800">Coming soon</span>
    </div>
);

    export const SuiteDashboard: React.FC<SuiteDashboardProps> = (props) => {
        const { 
            suiteName, 
            riskData, 
            alerts, 
            initialWorkflowId, 
            navParams, 
            onBack, 
            onRequestHelp, 
            onNavigate,
            onOpenGlossary,
            srmProject: propSrmProject,
            srmModule: propSrmModule
        } = props;    
        const { getAssetsByClient } = useGlobalAssets();

        // Normalize suiteName and ID:
        const resolvedSuiteId = Object.keys(SUITE_DETAILS).find(key => 
            key === suiteName || SUITE_DETAILS[key].name === suiteName
        ) || 'srm';
        const resolvedSuiteName = SUITE_DETAILS[resolvedSuiteId]?.name || suiteName;

    const { isLoaded } = useGoogleMaps();
    const requestedWorkflowId = initialWorkflowId || navParams?.activeWorkflowId || navParams?.workflowId || null;
    const startingWorkflowId = SUITE_WORKFLOWS[resolvedSuiteId]?.some(workflow => workflow.id === requestedWorkflowId && !workflow.comingSoon)
        ? requestedWorkflowId : null;
    const [activeWorkflow, setActiveWorkflow] = useState<string | null>(startingWorkflowId);
    React.useLayoutEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, [activeWorkflow]);

    const [currentSrmProject, setCurrentSrmProject] = useState<SrmProject | null>(propSrmProject || navParams?.srmProject || null);
    const [currentSrmModule, setCurrentSrmModule] = useState<SrmModule | null>(propSrmModule || navParams?.srmModule || null);
    const [formData, setFormData] = useState<FormState>(() => {
        const initial = navParams?.initialData || {};
        const proj = propSrmProject || navParams?.srmProject;
        if (proj && !initial.principal_name) {
            initial.principal_name = proj.name || (proj as any).projectName || '';
        }
        // FSAT specific pre-filling
        const fsatWorkflowIds = ['facility_security_assessment', 'facility_risk_assessment', 'facility_security_assessment_tool'];
        if (proj && startingWorkflowId && fsatWorkflowIds.includes(startingWorkflowId) && !initial.fsat_facility_name) {
            initial.fsat_facility_name = proj.name || (proj as any).projectName || '';
        }
        return initial as FormState;
    });
    const isCreatingProjectRef = useRef(false);

    // Sync activeWorkflow from navParams if it changes (e.g., deep linking)
    useEffect(() => {
        const workflowId = navParams?.activeWorkflowId || navParams?.workflowId || navParams?.workflow;
        if (workflowId) {
            console.log("SuiteDashboard: Syncing activeWorkflow from navParams:", workflowId);
            
            // Step 1: Normalize common alias mismatches
            let normalizedId = workflowId;
            const normalizationMap: Record<string, string> = {
                'facility_security_assessment': 'facility_risk_assessment',
                'facility_security_assessment_tool': 'facility_risk_assessment',
                'integrated_threat_analysis_hub': 'integrated_threat_analysis',
                'threat_group_osint': 'threat_group_network_analysis',
                'daily_micro_intelligence': 'daily_micro_report',
                'crisis_management': 'crisis_management_plan',
                'business_resiliency': 'business_resiliency_framework',
                'emergency_preparedness': 'emergency_preparedness_plan',
                'executive_protection_threat_analysis': 'ep_threat_analysis'
            };
            
            if (normalizationMap[workflowId]) {
                normalizedId = normalizationMap[workflowId];
            }
            
            // Step 2: Handle specialized workflows that open modals
            if (normalizedId === 'crisis_management_plan') setIsCrisisBuilderOpen(true);
            else if (normalizedId === 'business_resiliency_framework') setIsResiliencyBuilderOpen(true);
            else if (normalizedId === 'emergency_preparedness_plan') setIsEmergencyBuilderOpen(true);
            else if (normalizedId === 'local_incident_management_checklist') setIsLocalIncidentBuilderOpen(true);
            else if (normalizedId === 'crisis_template_builder') setIsCrisisTemplateBuilderOpen(true);
            
            // ALWAYS set activeWorkflow to ensure UI context matches the tool type
            setActiveWorkflow(normalizedId);

            // If we have initial data (e.g. from rerun), set it
            if (navParams?.initialData) {
                setFormData(navParams.initialData as FormState);
            }
        }
    }, [navParams?.activeWorkflowId, navParams?.workflowId, navParams?.workflow]);

    // Auto-route when arriving from CountryIntelPage report buttons
    useEffect(() => {
        if (navParams?.autoRunReport && navParams?.preselectedCountry) {
            setPreselectedCountryIso3(navParams.preselectedCountry);
            setPendingAutoReport(navParams.autoRunReport);
            setActiveWorkflow('integrated_threat_analysis');
        }
    }, [navParams?.autoRunReport, navParams?.preselectedCountry]);

    const [isLoading, setIsLoading] = useState(false);
    const [isBackgroundLoading, setIsBackgroundLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<AnalysisOrPlanResult | null>(null);
    const [sources, setSources] = useState<GroundingChunk[]>([]);
    const [planDataForAudit, setPlanDataForAudit] = useState<FormState | null>(null);
    const [showSavedReports, setShowSavedReports] = useState(false);
    // CountryIntelPage deep-link state
    const [preselectedCountryIso3, setPreselectedCountryIso3] = useState<string | null>(navParams?.preselectedCountry || null);
    const [pendingAutoReport, setPendingAutoReport] = useState<string | null>(navParams?.autoRunReport || null);

    // CMP Modal States
    const [isCrisisBuilderOpen, setIsCrisisBuilderOpen] = useState(false);
    const [isResiliencyBuilderOpen, setIsResiliencyBuilderOpen] = useState(false);
    const [isEmergencyBuilderOpen, setIsEmergencyBuilderOpen] = useState(false);
    const [isLocalIncidentBuilderOpen, setIsLocalIncidentBuilderOpen] = useState(false);
    const [isCrisisTemplateBuilderOpen, setIsCrisisTemplateBuilderOpen] = useState(false);
    const [linkedChecklist, setLinkedChecklist] = useState<string>();
    const [linkedIncidentType, setLinkedIncidentType] = useState<string>();

    // ✅ Refactored content selection logic
    let content: React.ReactNode = null;

    const [epProjects, setEpProjects] = useState<EpProject[]>([]);
    const [selectedEpProject, setSelectedEpProject] = useState<EpProject | null>(null);
    const [vcmcState, setVcmcState] = useState<any>(null); 

    // Integrated Workflow Tracking
    const [subWorkflowOrigin, setSubWorkflowOrigin] = useState<string | null>(null);
    const [subWorkflowPrefill, setSubWorkflowPrefill] = useState<FormState | null>(null);

    // --- SRM CONTEXT HYDRATION ---
    const [hydratedProject, setHydratedProject] = useState<SrmProject | null>(null);
    const [hydratedModule, setHydratedModule] = useState<SrmModule | null>(null);
    const [isHydrating, setIsHydrating] = useState(false);

    // Resolve project/module context (Favor hydrated state, then props if they are objects)
    const projectContext = React.useMemo(() => 
        hydratedProject || (typeof navParams?.srmProject === 'object' ? navParams?.srmProject as SrmProject : null)
    , [hydratedProject, navParams?.srmProject]);
    
    const currentModule = React.useMemo(() => 
        hydratedModule || 
        (typeof navParams?.srmModule === 'object' ? navParams?.srmModule as SrmModule : null) ||
        projectContext?.modules?.find(m =>
            m.id === navParams?.targetModuleId ||
            m.workflowId === activeWorkflow ||
            m.id === (typeof navParams?.srmModule === 'string' ? navParams?.srmModule : '')
        )
    , [hydratedModule, navParams?.srmModule, projectContext, navParams?.targetModuleId, activeWorkflow]);

    // --- SRM Hydration Effect ---
    useEffect(() => {
        const hydrateContext = async () => {
            const projectId = (typeof navParams?.srmProject === 'string' ? navParams.srmProject : null) || navParams?.projectId || navParams?.srmProject?.id;
            const moduleId = (typeof navParams?.srmModule === 'string' ? navParams.srmModule : null) || navParams?.targetModuleId || navParams?.moduleId;

            if (!db || (!projectId && !moduleId)) return;

            setIsHydrating(true);
            try {
                let fetchedProject: SrmProject | null = null;
                if (projectId) {
                    const projectDoc = await getDoc(doc(db, 'srm_projects', projectId));
                    if (projectDoc.exists()) {
                        fetchedProject = { id: projectDoc.id, ...projectDoc.data() } as SrmProject;
                        setHydratedProject(fetchedProject);
                        console.log("SuiteDashboard: Hydrated project:", projectId);
                    }
                }

            if (navParams?.srmProject?.modules) {
                const targetProject = fetchedProject || projectContext;
                const mod = targetProject?.modules?.find((m: any) => m.id === moduleId || (moduleId && m.id?.includes(moduleId)) || m.workflowId === activeWorkflow);
                if (mod) {
                    setHydratedModule(mod);
                    // If it has a result, load it
                    if (mod.resultData) {
                        setResult(mod.resultData);
                        // Also sync prefillData if we don't have it in state
                        if (mod.prefillData && Object.keys(formData).length === 0) {
                            setFormData(mod.prefillData);
                        }
                    }
                    console.log("SuiteDashboard: Hydrated module:", moduleId || activeWorkflow);
                }
            }
            } catch (err) {
                console.error("Error hydrating SuiteDashboard context:", err);
            } finally {
                setIsHydrating(false);
            }
        };

        hydrateContext();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        typeof navParams?.srmProject === 'string' ? navParams.srmProject : navParams?.srmProject?.id,
        typeof navParams?.srmModule === 'string' ? navParams.srmModule : navParams?.srmModule?.id,
        navParams?.targetModuleId,
        db
    ]);

    const frameworkContextId = navParams?.frameworkComponentId;
    const frameworkComponent = props.activeFramework?.components?.find(c => c.id === frameworkContextId);

    // Track the ID of the actively loaded framework component so we don't continuously overwrite new generations
    const initializedFrameworkRef = React.useRef<string | null>(null);
    const initializedModuleRef = React.useRef<string | null>(null);

    // Load result from srmModule if it exists
    useEffect(() => {
        const srmModule = navParams?.srmModule;
        if (srmModule && typeof srmModule === 'object' && srmModule.resultData && initializedModuleRef.current !== srmModule.id) {
            console.log("SuiteDashboard: Loading existing result from module:", srmModule.id);
            setResult(srmModule.resultData);
            initializedModuleRef.current = srmModule.id;
        }
    }, [navParams?.srmModule?.id, navParams?.srmModule]); // Watch both ID and ref

    useEffect(() => {
        if (projectContext && currentModule) {
            setFormData(prev => {
                if (Object.keys(prev).length === 0) {
                    return { ...currentModule.prefillData };
                }
                return prev;
            });
        }
    }, [projectContext?.id, currentModule?.id]); // Isolated ID-based effect

    useEffect(() => {
        if (frameworkComponent && frameworkComponent.data) {
             // Only load the saved framework data once when first navigating to it
             if (initializedFrameworkRef.current !== frameworkComponent.id) {
                 if ('reportType' in frameworkComponent.data) {
                     setResult(frameworkComponent.data as AnalysisOrPlanResult);
                 } else {
                     setFormData(frameworkComponent.data as FormState);
                 }
                 initializedFrameworkRef.current = frameworkComponent.id;
             }
        }
    }, [frameworkComponent]); // Isolated framework component effect

    useEffect(() => {
        // Only auto-launch a workflowId if it was explicitly passed in navParams
        // OR if initialWorkflowId was provided via direct page navigation
        const paramsWorkflowId = props.navParams?.workflowId || initialWorkflowId;
        if (!paramsWorkflowId) return;

        const suiteWorkflows = SUITE_WORKFLOWS[resolvedSuiteId] || [];
        const validWorkflowIds = suiteWorkflows.filter(w => !w.comingSoon).map(w => w.id);

        if (validWorkflowIds.includes(paramsWorkflowId)) {
            console.log(`SuiteDashboard: Auto-launching ${paramsWorkflowId} in ${resolvedSuiteId}`);
            
            // Handle builders that use specific modal states
            if (paramsWorkflowId === 'crisis_management_plan') setIsCrisisBuilderOpen(true);
            else if (paramsWorkflowId === 'business_resiliency_framework') setIsResiliencyBuilderOpen(true);
            else if (paramsWorkflowId === 'emergency_preparedness_plan') setIsEmergencyBuilderOpen(true);
            else if (paramsWorkflowId === 'local_incident_management_checklist') setIsLocalIncidentBuilderOpen(true);
            else if (paramsWorkflowId === 'crisis_template_builder') setIsCrisisTemplateBuilderOpen(true);
            
            // Always set activeWorkflow for consistency
            setActiveWorkflow(paramsWorkflowId);
        }
    }, [initialWorkflowId, props.navParams?.workflowId, resolvedSuiteId]);

    useEffect(() => {
        if (suiteName === 'Executive Protection' || activeWorkflow === 'project_review') {
            const fetchEpProjects = async () => {
                if (!db || !props.currentOrganization) {
                    console.error("Firestore DB not initialized or no organization found.");
                    return;
                }
                try {
                    const q = query(
                        collection(db, 'ep_projects'), 
                        where('organizationId', '==', props.currentOrganization.id)
                    );
                    const querySnapshot = await getDocs(q);
                    const projects = querySnapshot.docs.map((doc: any) => ({ id: doc.id, ...doc.data() } as EpProject));
                    setEpProjects(projects);
                } catch (e) {
                    console.error("Error fetching EP projects:", e);
                }
            };
            fetchEpProjects();
        }
    }, [suiteName, activeWorkflow, props.currentOrganization]);


    const handleWorkflowSelect = (workflowId: string) => {
        if (workflowId === 'hsse_compliance_planner' || workflowId === 'hsse_incident_report' || workflowId === 'hsse_suite_link' || workflowId === 'hsse_compliance_briefing' || workflowId === 'compliance_audit')
            return;
        if (workflowId === 'av_tools_link') {
            props.onOpenAvToolsModal();
            return;
        }
        if (workflowId === 'lms_dashboard') {
             props.onNavigateToSuite('Learning Management Suite');
             return;
        }
        if (workflowId === 'trm_suite_link') {
             props.onNavigateToSuite('Travel Risk Management');
             return;
        }
        
        if (workflowId === 'vcmc_simulation_link') {
             props.onNavigateToSuite('VCMC Simulation'); 
             return;
        }
        if (['integrated_threat_analysis', 'geopolitical_analysis', 'threat_group_osint'].includes(workflowId)) {
            setActiveWorkflow(workflowId);
            setFormData({});
            setResult(null);
            return;
        }
        if (workflowId === 'crisis_management_plan') {
            setIsCrisisBuilderOpen(true);
            return;
        }
        if (workflowId === 'business_resiliency_framework') {
            setIsResiliencyBuilderOpen(true);
            return;
        }
        if (workflowId === 'emergency_preparedness_plan') {
            setIsEmergencyBuilderOpen(true);
            return;
        }
        if (workflowId === 'local_incident_management_checklist') {
            setIsLocalIncidentBuilderOpen(true);
            return;
        }
        if (workflowId === 'crisis_template_builder') {
            setIsCrisisTemplateBuilderOpen(true);
            return;
        }
        
         
        setActiveWorkflow(workflowId);
        setFormData({});
        setResult(null);
        setError(null);
    };

    const handleFormChange = useCallback((event: any) => {
        const { name, value, type, checked } = event.target;
        setFormData(prev => ({ 
            ...prev, 
            [name]: type === 'checkbox' ? checked : value 
        }));
    }, []);
    
    // --- AUTOSAVE LOGIC ---
    const autosaveTimerRef = useRef<NodeJS.Timeout | null>(null);
    const lastSavedDataRef = useRef<string>('');

    useEffect(() => {
        if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
        const currentDataStr = JSON.stringify(formData);
        if (currentDataStr === lastSavedDataRef.current) return;

        autosaveTimerRef.current = setTimeout(async () => {
            try {
                // Ensure we still have the context when the timer fires
                if (!activeWorkflow || Object.keys(formData).length === 0) return;

                const workflows = SUITE_WORKFLOWS[resolvedSuiteId] || [];
                const workflowDetails = workflows.find(w => w.id === activeWorkflow);
                const workflowLabel = workflowDetails?.name || activeWorkflow;
                
                // Construct a descriptive name
                const locationLabel = formData.country || formData.location?.address || formData.ep_ops_location?.address || 'General';
                const projectName = `${locationLabel} - ${workflowLabel}`;

                const targetProject = currentSrmProject || hydratedProject || (typeof navParams?.srmProject === 'object' ? navParams?.srmProject as SrmProject : null);

                if (targetProject?.id) {
                    // Update existing project
                    const projectRef = doc(db, 'srm_projects', targetProject.id);
                    const projectSnap = await getDoc(projectRef);
                    if (projectSnap.exists()) {
                        const projectData = projectSnap.data();
                        const updatedModules = (projectData.modules || []).map((m: any) => 
                            m.workflowId === activeWorkflow ? { ...m, prefillData: formData } : m
                        );
                        
                        // If current module doesn't exist in the project yet, add it
                        const moduleExists = updatedModules.some((m: any) => m.workflowId === activeWorkflow);
                        if (!moduleExists) {
                            updatedModules.push({
                                id: crypto.randomUUID().split('-')[0],
                                workflowId: activeWorkflow,
                                status: 'Draft',
                                prefillData: formData
                            });
                        }

                        await updateDoc(projectRef, { 
                            modules: updatedModules,
                            updatedAt: new Date().toISOString(),
                            name: projectData.name || projectName
                        });
                        lastSavedDataRef.current = currentDataStr;
                        console.log("SuiteDashboard: Autosaved to project:", targetProject.id);
                    }
                } else if (!targetProject && !isCreatingProjectRef.current && !navParams?.projectId) {
                    // Create a new "In-Progress" project if one doesn't exist contextually
                    if (Object.keys(formData).length > 1) { // Require at least 2 fields to avoid empty saves
                        isCreatingProjectRef.current = true;
                        const newProject = {
                            name: projectName,
                            organizationId: props.currentOrganization!.id,
                            userId: props.currentUser!.uid,
                            createdAt: new Date().toISOString(),
                            updatedAt: new Date().toISOString(),
                            status: 'Draft',
                            modules: [{
                                id: crypto.randomUUID().split('-')[0],
                                workflowId: activeWorkflow,
                                status: 'Draft',
                                prefillData: formData
                            }]
                        };
                        const docRef = await addDoc(collection(db, 'srm_projects'), newProject);
                        lastSavedDataRef.current = currentDataStr;
                        
                        // Update local project context immediately
                        const newHydratedProject = { id: docRef.id, ...newProject } as any;
                        setHydratedProject(newHydratedProject);
                        setCurrentSrmProject(newHydratedProject);
                        
                        console.log("SuiteDashboard: Created new SRM draft:", docRef.id);

                        // Update nav params so subsequent saves go to this same doc
                        onNavigate({ 
                            page: 'suiteDashboard', 
                            params: { 
                                ...navParams, 
                                suiteName: resolvedSuiteName,
                                srmProject: newHydratedProject 
                            } 
                        });
                    }
                }
            } catch (err) {
                console.error("Autosave error:", err);
            } finally {
                // We keep isCreatingProjectRef true for the lifecycle of this first creation
            }
        }, 5000); // 5-second debounce for stability

        return () => {
            if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
        };
    }, [formData, activeWorkflow, props.currentUser, props.currentOrganization, suiteName, navParams, onNavigate]);


    const handleSubmit = async (e: React.FormEvent, reportData?: any) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);
        setSources([]);
    
        const workflows = SUITE_WORKFLOWS[resolvedSuiteId] || [];
        const workflowDetails = workflows.find(w => w.id === activeWorkflow);
        const analysisPrompt = workflowDetails?.name || activeWorkflow || suiteName;
        const analysisType = activeWorkflow === 'country_geopolitical_analysis'
            ? 'Comprehensive Country Geopolitical & Risk Analysis'
            : analysisPrompt;
            
        console.log("SuiteDashboard: Submitting Analysis Request", {
            analysisType,
            activeWorkflow,
            suiteName,
            payloadKeys: Object.keys(formData)
        });
    
        try {
            const functions = getFunctions();
            
            // ROUTING LOGIC: Analytics and Composite reports use the specialized executive report generator
            const isExecutiveOrAnalytics = reportData || activeWorkflow === 'composite_report' || activeWorkflow === 'composite_analytics';
            
            if (isExecutiveOrAnalytics) {
                const generateExecutiveReport = httpsCallable(functions, 'generateExecutiveReport', { timeout: 540000 });
                const config = buildExecutiveReportConfig(activeWorkflow, reportData, formData);
                const payload = {
                    organizationId: props.currentOrganization?.id,
                    config,
                };
                
                const response: any = await generateExecutiveReport(payload);
                const { reportId, reportData: resultData } = readExecutiveReportResponse(response.data);

                if (reportId) {
                    onNavigate?.({ page: 'executiveReport', params: { reportId, config } });
                    return;
                }
                
                // If it's an operational brief, navigate to the high-fidelity viewer immediately
                if (resultData?.analysisType === 'operational_analytics') {
                    onNavigate?.({ 
                        page: 'executiveReport', 
                        params: { 
                            reportData: resultData, 
                            config,
                        } 
                    });
                } else {
                    // Intelligence reports still use the structural AnalysisResultDisplay
                    setResult(resultData);
                    setSources([]);
                }
                return;
            }

            const onDemandAnalysisProxy = httpsCallable(functions, 'onDemandAnalysisProxy', { timeout: 540000 });
            
            // ✅ Agency Context Injection: Scope AI output to the active client when present
            const activeClientId = navParams?.activeClientId || navParams?.clientId;
            const activeClientName = navParams?.activeClientName || navParams?.clientName;
            const activeClientIndustry = navParams?.activeClientIndustry || navParams?.clientIndustry;
            const activeClientCountries = navParams?.activeClientCountries || navParams?.clientCountries;

            const clientContextPrefix = activeClientId
                ? `[AGENCY CLIENT CONTEXT] This analysis is prepared FOR a client named "${activeClientName}"` +
                  (activeClientIndustry ? ` in the ${activeClientIndustry} industry` : '') +
                  (activeClientCountries?.length ? `, operating in: ${activeClientCountries.join(', ')}` : '') +
                  '. Tailor all findings and recommendations specifically to this client context. '
                : '';

            const payload = {
                analysisType: analysisType,
                formData: {
                    ...formData,
                    // Inject client context so the function can ground the prompts
                    clientContext: activeClientId ? {
                        clientId: activeClientId,
                        clientName: activeClientName,
                        industry: activeClientIndustry,
                        countries: activeClientCountries,
                    } : undefined,
                    contextPrefix: clientContextPrefix || undefined,
                },
                forceRefresh: !!formData.forceRegenerate,
            };

            console.log("SuiteDashboard: Submitting onDemandAnalysisProxy with payload:", payload);

            const response: any = await onDemandAnalysisProxy(payload);
            const analysisResult = response.data.result;

            setResult(analysisResult);
            await recordGeneratedReport(props.currentOrganization?.id, analysisResult, analysisResult?.title || analysisType, activeWorkflow || 'analysis', activeClientId);
            // Note: The proxy doesn't return sources yet, so this will be empty.
            // This can be added to the proxy function later if needed.
            setSources([]);
    
        } catch (err: any) {
            setError(err.message || "An error occurred during analysis.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleApproveEpProject = async (projectId: string) => {
        if (!db) return;
        try {
            const docRef = doc(db, 'ep_projects', projectId);
            await updateDoc(docRef, { status: 'Active' });
            setSelectedEpProject(prev => prev ? { ...prev, status: 'Active' } : null);
        } catch (e: any) {
            console.error("Failed to approve project:", e);
            setError("Failed to approve mission: " + e.message);
        }
    };

    const handleInitializeEpOperation = async (plannerData: FormState) => {
        if (!db) {
            console.error("Firestore DB not initialized. Cannot initialize EP operation.");
            setError("Database connection failed. Please try again later.");
            return;
        }
        try {
            const opord = await generateEpOperationOrder(plannerData);
            
            let locationData = undefined;
            const locString = [plannerData.ep_pp_location_address, plannerData.ep_pp_location_city, plannerData.ep_pp_location_country].filter(Boolean).join(', ');
            if (locString) {
                try {
                    const coords = await geocode(locString);
                    locationData = { lat: coords.lat, lng: coords.lng, address: locString };
                } catch (e) {
                    console.warn("Could not geocode EP location", e);
                }
            }

            const newProject: Omit<EpProject, 'id'> = {
                name: plannerData.ep_pp_project_name || 'Untitled Operation',
                status: 'Pending', // Changed from 'Active' to require approval
                organizationId: props.currentOrganization!.id,
                plannerData: plannerData,
                opordContent: opord,
                location: locationData,
                createdAt: new Date().toISOString()
            };

            const docRef = await addDoc(collection(db, 'ep_projects'), newProject);
            const projectWithId = { ...newProject, id: docRef.id } as EpProject;

            setSelectedEpProject(projectWithId);
setActiveWorkflow('project_review');

        } catch (e: any) {
            console.error("Failed to initialize EP Operation:", e);
            setError("Failed to initialize operation: " + e.message);
        }
    };

    const handleSaveToFramework = async () => {
        if (!projectContext || !currentModule || !result || !props.onUpdateSrmProjectModule) return;
        
        await props.onUpdateSrmProjectModule(projectContext.id, currentModule.id, result);
        
        onNavigate({ page: 'srm_command_center', params: { view: 'dashboard' } });
    };

    const handleSaveToActiveFramework = async () => {
        if (!frameworkContextId || !props.onUpdateFrameworkComponent || !result) return;
        
        await props.onUpdateFrameworkComponent(frameworkContextId, {
            data: result,
            status: 'Active' 
        });
        
        onNavigate({ page: 'srm_command_center', params: { projectId: props.activeFramework?.projectId } });
    };

    const handleConvertAnalysisToProject = async (analysis: EpThreatAnalysisResult) => {
        setIsLoading(true);
        try {
            const recommendations = await generateEpTacticalRecommendations(analysis);

            const projectData: FormState = {
                ep_pp_project_name: `Protection Detail: ${analysis.principalName}`,
                ep_pp_principal_name: analysis.principalName,
                ep_pp_threat_summary: `Threat Level: ${analysis.threatLevel}. ${analysis.executiveSummary}`,
                ep_pp_location_country: analysis.location || 'Unknown',
                ep_pp_start_date: localDateInputValue(),
                ep_pp_personnel_reqs: recommendations.personnel,
                ep_pp_logistics_reqs: recommendations.logistics
            };

            setActiveWorkflow('project_planner');
            setFormData(projectData);
            setResult(null);
            setSubWorkflowOrigin(null); // Clear sub-workflow if we convert to a new project
        } catch (e) {
            console.error("Failed to generate tactical recommendations:", e);
            void showAppAlert("Failed to generate tactical recommendations from analysis.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleFinishSubWorkflow = () => {
        if (subWorkflowOrigin === 'project_planner' && result) {
            // Merge threat analysis result into planner form data
            const threatResult = result as any;
            setFormData(prev => ({
                ...prev,
                ep_pp_threat_level: threatResult.threatLevel,
                ep_pp_threat_analysis_summary: threatResult.executiveSummary,
                ep_pp_threat_analysis_data: threatResult, // Store the whole object for report use
                // Progress to stage 5 (report generation)
                current_stage: 5 
            }));
            setActiveWorkflow('project_planner');
            setResult(null);
            setSubWorkflowOrigin(null);
        }
    };

    const handleBackClick = () => {
        if (projectContext) {
            onNavigate({ 
                page: 'srm_command_center', 
                params: { 
                    view: 'dashboard', 
                    activeTab: navParams?.activeTab || 'components' 
                } 
            });
        } else if (frameworkContextId) {
            onNavigate({ 
                page: 'srm_command_center', 
                params: { 
                    projectId: props.activeFramework?.projectId, 
                    activeTab: navParams?.activeTab || 'components' 
                } 
            });
        } else {
            if (activeWorkflow) {
                setActiveWorkflow(null);
            } else {
                onBack();
            }
        }
    };
    
    // Use the correctly resolved ID for details and name for workflows
    const suiteDetails = SUITE_DETAILS[resolvedSuiteId] || { name: suiteName, description: '', icon: ICONS.folder, color: { bg: 'bg-slate-100', text: 'text-slate-600' } };
    const workflows = SUITE_WORKFLOWS[resolvedSuiteId] || [];
    const visibleWorkflows = workflows.filter(workflow => workflow.id !== 's01_fsat');

    // --- RENDER CONTENT BRANCHING ---
    if (activeWorkflow === 'integrated_threat_analysis') {
        content = (
            <IntegratedThreatAnalysis 
                db={db}
                onBack={() => setActiveWorkflow(null)}
                onNavigate={onNavigate}
                onOpenGlossary={onOpenGlossary}
                riskData={riskData} 
                alerts={alerts}
                srmProject={projectContext}
                srmModule={currentModule}
                params={{
                    ...navParams,
                    organizationId: props.currentOrganization?.id,
                    preselectedCountry: preselectedCountryIso3 || undefined,
                    autoRunReport: pendingAutoReport || undefined
                }}
            />
        );
    } else if (activeWorkflow === 'gtg_dashboard_link') {
        content = (
            <div className="p-6 bg-white rounded-xl shadow-sm h-full flex flex-col items-center justify-center text-center">
                <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418" /></svg>
                </div>
                <h2 className="text-2xl font-bold text-slate-800">Global Threat Group Dashboard</h2>
                {projectContext ? (
                    <>
                        <p className="text-slate-500 mt-2 mb-6 max-w-md">
                            Analyze active threat groups and regional instability relative to <span className="text-blue-600 font-bold">{projectContext.name}</span>.
                        </p>
                        <button 
                            onClick={() => onNavigate({ 
                                page: 'gtg_dashboard', 
                                params: { 
                                    projectContext: projectContext,
                                    assets: props.currentOrganization ? getAssetsByClient(props.currentOrganization.id) : [],
                                    preselectedCountry: projectContext.country || projectContext.locationAddress?.split(',').pop()?.trim() || undefined
                                } 
                            })}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition-colors flex items-center gap-2"
                        >
                            Run Contextual Asset Analysis &rarr;
                        </button>
                    </>
                ) : (
                    <>
                        <p className="text-slate-500 mt-2 mb-6 max-w-md">Access real-time intelligence on active threat groups and regional instability indicators.</p>
                        <button 
                            onClick={() => onNavigate({ page: 'gtg_dashboard' })}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition-colors"
                        >
                            Launch Dashboard &rarr;
                        </button>
                    </>
                )}
            </div>
        );
    } else if (['s05_country_intelligence', 's06_threat_group_briefing', 's07_daily_intelligence', 's12_pre_movement_intelligence'].includes(activeWorkflow || '')) {
        const serviceCode = ({ s05_country_intelligence: 'S-05', s06_threat_group_briefing: 'S-06',
            s07_daily_intelligence: 'S-07', s12_pre_movement_intelligence: 'S-12' } as const)[activeWorkflow as 's05_country_intelligence' | 's06_threat_group_briefing' | 's07_daily_intelligence' | 's12_pre_movement_intelligence'];
        content = props.currentOrganization?.id && navParams?.serviceRequestId
            ? <IntelligenceServiceWorkspace key={navParams.serviceRequestId} organizationId={props.currentOrganization.id}
                requestId={navParams.serviceRequestId} serviceCode={serviceCode}
                onBack={() => onNavigate({ page: 'organizationDashboard' })}
                onOpenReport={reportId => onNavigate({ page: 'analysisDisplay', params: { reportId } })} />
            : <div className="p-8 text-sm text-slate-600">Open this workspace from an accepted service request.</div>;
    } else if (activeWorkflow === 's02_facility_plan') {
        content = props.currentOrganization?.id && navParams?.serviceRequestId
            ? <S02FacilityPlanWorkspace key={navParams.serviceRequestId} organizationId={props.currentOrganization.id}
                requestId={navParams.serviceRequestId} onBack={() => onNavigate({ page: 'organizationDashboard' })}
                onOpenDashboard={projectId => onNavigate({ page: 'srm_command_center', params: { projectId, view: 'dashboard' } })}
                onOpenReport={reportId => onNavigate({ page: 'analysisDisplay', params: { reportId } })} />
            : <div className="p-8 text-sm text-slate-600">Open this workspace from an accepted S-02 service request.</div>;
    } else if (activeWorkflow === 's03_staff_protection_plan') {
        content = props.currentOrganization?.id && navParams?.serviceRequestId
            ? <S03StaffProtectionWorkspace key={navParams.serviceRequestId} organizationId={props.currentOrganization.id}
                requestId={navParams.serviceRequestId} onBack={() => onNavigate({ page: 'organizationDashboard' })}
                onOpenReport={reportId => onNavigate({ page: 'analysisDisplay', params: { reportId } })} />
            : <div className="p-8 text-sm text-slate-600">Open this workspace from an accepted S-03 service request.</div>;
    } else if (activeWorkflow === 's13_crisis_management') {
        content = props.currentOrganization?.id && navParams?.serviceRequestId
            ? <S13CrisisManagementWorkspace key={navParams.serviceRequestId} organizationId={props.currentOrganization.id}
                requestId={navParams.serviceRequestId} onBack={() => onNavigate({ page: 'organizationDashboard' })}
                onOpenReport={reportId => onNavigate({ page: 'analysisDisplay', params: { reportId } })} />
            : <div className="p-8 text-sm text-slate-600">Open this workspace from an accepted S-13 service request.</div>;
    } else if (activeWorkflow === 's15_hsse_compliance') {
        content = props.currentOrganization?.id && navParams?.serviceRequestId
            ? <S15HSSEComplianceWorkspace key={navParams.serviceRequestId} organizationId={props.currentOrganization.id}
                requestId={navParams.serviceRequestId} onBack={() => onNavigate({ page: 'organizationDashboard' })}
                onOpenReport={reportId => onNavigate({ page: 'analysisDisplay', params: { reportId } })} />
            : <div className="p-8 text-sm text-slate-600">Open this workspace from an accepted S-15 service request.</div>;
    } else if (activeWorkflow === 's16_emergency_preparedness') {
        content = props.currentOrganization?.id && navParams?.serviceRequestId
            ? <S16EmergencyPreparednessWorkspace key={navParams.serviceRequestId} organizationId={props.currentOrganization.id}
                requestId={navParams.serviceRequestId} onBack={() => onNavigate({ page: 'organizationDashboard' })}
                onOpenReport={reportId => onNavigate({ page: 'analysisDisplay', params: { reportId } })} />
            : <div className="p-8 text-sm text-slate-600">Open this workspace from an accepted S-16 service request.</div>;
    } else if (activeWorkflow === 's14_business_resiliency') {
        content = props.currentOrganization?.id && navParams?.serviceRequestId
            ? <S14BusinessResiliencyWorkspace key={navParams.serviceRequestId} organizationId={props.currentOrganization.id}
                requestId={navParams.serviceRequestId} onBack={() => onNavigate({ page: 'organizationDashboard' })}
                onOpenReport={reportId => onNavigate({ page: 'analysisDisplay', params: { reportId } })} />
            : <div className="p-8 text-sm text-slate-600">Open this workspace from an accepted S-14 service request.</div>;
    } else if (activeWorkflow === 's04_event_security_plan') {
        content = props.currentOrganization?.id && navParams?.serviceRequestId
            ? <S04EventSecurityWorkspace key={navParams.serviceRequestId} organizationId={props.currentOrganization.id}
                requestId={navParams.serviceRequestId} onBack={() => onNavigate({ page: 'organizationDashboard' })}
                onOpenDashboard={projectId => onNavigate({ page: 'srm_command_center', params: { projectId, view: 'dashboard' } })}
                onOpenReport={reportId => onNavigate({ page: 'analysisDisplay', params: { reportId } })} />
            : <div className="p-8 text-sm text-slate-600">Open this workspace from an accepted S-04 service request.</div>;
    } else if (activeWorkflow === 's01_fsat') {
        content = props.currentOrganization?.id && navParams?.serviceRequestId
            ? <S01AssessmentWorkspace key={navParams.serviceRequestId} organizationId={props.currentOrganization.id}
                requestId={navParams.serviceRequestId} onBack={() => onNavigate({ page: 'organizationDashboard' })} />
            : <div className="p-8 text-sm text-slate-600">Open this workspace from an accepted S-01 service request.</div>;
    } else if (activeWorkflow === 'trm_brief') {
        content = props.currentOrganization?.id
            ? navParams?.serviceRequestId
                ? <TrmBriefHub key={navParams.serviceRequestId} organizationId={props.currentOrganization.id} requestId={navParams.serviceRequestId}
                    onBack={() => onNavigate({ page: 'organizationDashboard' })} />
                : <TrmBriefRequestPicker organizationId={props.currentOrganization.id} onBack={() => onNavigate({ page: 'organizationDashboard' })}
                    onSelect={serviceRequestId => onNavigate({ page: 'suiteDashboard', params: { suiteName: 'Travel Risk Management', activeWorkflowId: 'trm_brief', serviceRequestId } })} />
            : <div className="p-8 text-sm text-slate-600">Loading organization…</div>;
    } else if (activeWorkflow === 'project_planner') {
        content = props.currentOrganization?.id
            ? navParams?.serviceRequestId
                ? <EpProjectHub key={navParams.serviceRequestId} organizationId={props.currentOrganization.id} requestId={navParams.serviceRequestId} onBack={() => onNavigate({ page: 'organizationDashboard' })} />
                : <EpProjectRequestPicker organizationId={props.currentOrganization.id} onBack={() => onNavigate({ page: 'organizationDashboard' })}
                    onSelect={serviceRequestId => onNavigate({ page: 'suiteDashboard', params: { suiteName: 'Executive Protection', activeWorkflowId: 'project_planner', serviceRequestId } })} />
            : <div className="p-8 text-sm text-slate-600">Loading organization…</div>;
    } else if (activeWorkflow === 'ep_advance_site_survey') {
        content = props.currentOrganization?.id
            ? navParams?.serviceRequestId
                ? <EpAdvanceWorkflow key={navParams.serviceRequestId} organizationId={props.currentOrganization.id} requestId={navParams.serviceRequestId} onBack={() => onNavigate({ page: 'organizationDashboard' })} />
                : <EpSurveyRequestPicker organizationId={props.currentOrganization.id} onBack={() => onNavigate({ page: 'organizationDashboard' })}
                    onSelect={serviceRequestId => onNavigate({ page: 'suiteDashboard', params: { suiteName: 'Executive Protection', activeWorkflowId: 'ep_advance_site_survey', serviceRequestId } })} />
            : <div className="p-8 text-sm text-slate-600">Loading organization…</div>;
    } else if (activeWorkflow === 'project_review') {
        if (selectedEpProject) {
            content = (
                <EpOperationsDashboard 
                    project={selectedEpProject} 
                    onBack={() => setSelectedEpProject(null)}
                    onApprove={() => handleApproveEpProject(selectedEpProject.id)}
                    onNavigateToGsoc={props.onNavigateToGsocDashboard}
                    onNavigateToVcmc={() => handleWorkflowSelect('vcmc_simulation_link')}
                />
            );
        } else {
            content = (
                <EpDashboard 
                    projects={epProjects} 
                    onSelectProject={(id) => {
                        const proj = epProjects.find(p => p.id === id);
                        if (proj) setSelectedEpProject(proj);
                    }} 
                    onCreateProject={() => setActiveWorkflow('project_planner')}
                    onDeleteProject={() => {}}
                />
            );
        }
    } else if (suiteName === 'VCMC Simulation') {
        if (vcmcState) {
             content = <VCMC initialState={vcmcState} onExit={() => setVcmcState(null)} />;
        } else {
            content = (
                <VCMCSetup onSimulationStart={setVcmcState} />
            );
        }
    } else if (activeWorkflow === 'competencyBuilder' || navParams?.workflow === 'competencyBuilder') {
        content = (
            <IndustryCompetencyBuilder 
                industryName={navParams?.industryName || 'Custom'}
                recommendations={navParams?.recommendations as CompetencyRecommendationResult}
                onBack={onBack}
                onCreateCustomCourse={(c, sid) => props.onCreateCustomCourse && props.onCreateCustomCourse(c, sid)}
                students={props.students || []}
                onCreateStudent={props.onCreateStudent as any} 
            />
        );
    } else if (suiteName === 'Learning Management Suite' || suiteName === 'Learning Management Suite (LMS)') {
        const lmsProps = {
           ...props,
           maintenanceMode: false,
           workflowName: "LMS Dashboard",
           formData: formData,
           isLoading: isLoading,
           error: error,
           onSubmit: handleSubmit,
           onFormChange: handleFormChange,
           workflowId: activeWorkflow || '', 
        };
        content = (
            <AnalysisForm 
                {...lmsProps} 
                onRequestHelp={onRequestHelp} 
                onNavigate={onNavigate} 
                srmProject={projectContext || undefined}
                srmModule={currentModule || undefined}
            />
        );
    } else if (showSavedReports) {
        content = (
            <>
                <button onClick={() => setShowSavedReports(false)} className="mb-6 flex items-center text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox=" 0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                    Back to {suiteName}
                </button>
                <SavedReportsList 
                    organizationId={props.currentOrganization?.id || ''}
                    readOnly={!props.currentUser?.adminRole}
                    onSelectReport={(report) => {
                        setResult(report);
                        setShowSavedReports(false);
                        setActiveWorkflow(null);
                    }}
                    onBack={() => setShowSavedReports(false)}
                />
            </>
        );
    } else if (result) {
        content = (
            <>
                <button
                    onClick={() => {
                        if (projectContext) {
                            onNavigate({ page: 'srm_command_center', params: { view: 'dashboard' } });
                        } else {
                            setResult(null);
                            setActiveWorkflow(null);
                        }
                    }}
                    className="mb-4 flex items-center text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox=" 0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                    {projectContext ? `Back to ${projectContext.name || projectContext.projectName} Blueprint` : `Back to ${suiteName}`}
                </button>

                {projectContext && currentModule && props.onUpdateSrmProjectModule && (
                    <div className="mb-6 flex items-center justify-between gap-4 bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-4 shadow-sm">
                        <div className="flex items-start gap-3">
                            <div className="mt-0.5 p-2 bg-emerald-600 text-white rounded-lg flex-shrink-0">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div>
                                <p className="font-bold text-emerald-900 text-sm">Analysis Complete — Ready to Save to Framework</p>
                                <p className="text-emerald-700 text-xs mt-0.5">
                                    Saving <span className="font-semibold">{currentModule.title}</span> to <span className="font-semibold">{projectContext.name || projectContext.projectName}</span> will mark this component Ready and return you to the blueprint.
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={handleSaveToFramework}
                            disabled={isLoading}
                            className="flex-shrink-0 flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-5 py-2.5 rounded-lg shadow-md shadow-emerald-200 transition-all disabled:opacity-50 whitespace-nowrap"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            Save to Framework &amp; Return
                        </button>
                    </div>
                )}

                <div className="mb-6 border-b pb-4">
                    <h2 className="text-2xl font-bold text-slate-800">{activeWorkflow ? SUITE_WORKFLOWS[resolvedSuiteId]?.find(w => w.id === activeWorkflow)?.name : 'Saved Report View'}</h2>
                    <p className="text-slate-500">{activeWorkflow ? 'Real-time Strategic Intelligence' : 'Viewing retrieved report from archive.'}</p>
                </div>
                <AnalysisResultDisplay 
                    result={result} 
                    sources={[]}
                    activeWorkflow={activeWorkflow || undefined}
                    onNavigate={onNavigate}
                    onSave={() => Promise.resolve()} 
                    isSaving={false}                 
                    onConvertAnalysisToProject={handleConvertAnalysisToProject}
                    srmProject={projectContext || props.navParams?.srmProject}
                    srmModule={currentModule || props.navParams?.srmModule}
                    organizationId={props.currentOrganization?.id || projectContext?.organizationId}
                    clientId={projectContext?.clientId}
                    onSaveToFramework={props.onUpdateSrmProjectModule}
                    onLaunchChecklist={(checklistName) => {
                        setLinkedChecklist(checklistName);
                        setLinkedIncidentType(String((result as any)?.incidentType || ''));
                        setIsLocalIncidentBuilderOpen(true);
                    }}
                />
            </>
        );
    } else if (!activeWorkflow) {
        content = (
            <>
                <button onClick={handleBackClick} className="mb-6 flex items-center text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox=" 0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                    {projectContext ? `Back to ${projectContext.name} Blueprint` : (frameworkContextId ? 'Back to Command Center' : 'Back to Dashboard')}
                </button>
                
                <div className="mb-8 flex items-center">
                    <div className={`p-3 rounded-lg ${suiteDetails.color.bg} ${suiteDetails.color.text} mr-4`}>
                        {React.cloneElement(suiteDetails.icon as React.ReactElement<any>, { className: 'h-8 w-8' })}
                    </div>
                    <div>
                        <h2 className="text-3xl font-bold text-slate-800">{suiteDetails.name}</h2>
                        <p className="text-slate-500 mt-1">{suiteDetails.description}</p>
                    </div>
                </div>

                {suiteName === 'Report Generation Suite' && (
                    <div className="mb-8">
                        <button 
                            onClick={() => setShowSavedReports(true)}
                            className="bg-white border border-slate-300 text-slate-700 font-semibold py-3 px-5 rounded-lg hover:bg-slate-50 flex items-center shadow-sm transition-all hover:shadow-md w-full sm:w-auto"
                        >
                            {React.cloneElement(ICONS.folder as any, { className: 'h-5 w-5 mr-3 text-blue-600' })}
                            Open Saved Reports Library
                        </button>
                    </div>
                )}

                {resolvedSuiteId === 'cmp' && (
                    <div className="mb-8 bg-slate-900 rounded-xl p-6 shadow-sm border border-slate-700 text-white flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between relative overflow-hidden">
                        <div className="absolute top-0 right-0 p-4 opacity-5 bg-blue-500 w-48 h-48 rounded-bl-full pointer-events-none" />
                        <div className="z-10">
                            <h3 className="text-lg font-bold text-slate-200 mb-2 flex items-center gap-2">
                                <span className="text-blue-400">{ICONS.shield}</span> Chief Security Officer (CSO) Readiness Posture
                            </h3>
                            <p className="text-sm text-slate-400 max-w-2xl">
                                Synthesizing real-time cross-plan intelligence via the <span className="text-blue-300 font-semibold">Resiliency Mesh</span>. Tactical-to-strategic linking is currently active, bridging Crisis Management objectives with actionable Local Incident Checklists.
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-4 z-10 w-full lg:w-auto">
                            <div className="bg-slate-800 border border-slate-700 p-4 rounded-lg flex-1 lg:flex-none text-center min-w-[120px] shadow-inner">
                                <div className="text-2xl font-black text-emerald-400 flex justify-center gap-1 items-center">
                                    <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> Optimal
                                </div>
                                <div className="text-xs text-slate-500 font-bold uppercase tracking-wider mt-1">System State</div>
                            </div>
                            <div className="bg-slate-800 border border-slate-700 p-4 rounded-lg flex-1 lg:flex-none text-center min-w-[120px] shadow-inner">
                                <div className="text-2xl font-black text-blue-400">Active</div>
                                <div className="text-xs text-slate-500 font-bold uppercase tracking-wider mt-1">Cross-Plan Sync</div>
                            </div>
                        </div>
                    </div>
                )}

                <h3 className="text-lg font-semibold text-slate-700 mb-4">Select a Workflow</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {visibleWorkflows.map(wf => wf.comingSoon
                        ? <ComingSoonWorkflowCard key={wf.id} workflow={wf} />
                        : <WorkflowCard key={wf.id} workflow={wf} onClick={() => handleWorkflowSelect(wf.id)} />)}
                    {visibleWorkflows.length === 0 && <p className="text-slate-500 italic">No workflows available for this suite yet.</p>}
                </div>
            </>
        );
    } else {
        const workflowDetails = workflows.find(w => w.id === activeWorkflow);
        content = (
            <>
                <button onClick={handleBackClick} className="mb-6 flex items-center text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox=" 0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                    {projectContext ? `Back to ${projectContext.name} Blueprint` : (frameworkContextId ? 'Back to Command Center' : `Back to ${suiteName}`)}
                </button>

                <div className="mb-6 border-b pb-4">
                    <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
                        {workflowDetails?.name || activeWorkflow}
                        {projectContext && (
                            <span className="text-xs bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full border border-emerald-200 uppercase tracking-wider font-bold flex items-center gap-1">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9.243 3.03a1 1 0 01.727 1.213L9.53 6h2.94l.56-2.243a1 1 0 111.94.486L14.53 6H17a1 1 0 110 2h-2.97l-1 4H15a1 1 0 110 2h-2.47l-.56 2.242a1 1 0 11-1.94-.485L10.47 14H7.53l-.56 2.242a1 1 0 11-1.94-.485L5.47 14H3a1 1 0 110-2h2.97l1-4H5a1 1 0 110-2h2.47l.56-2.243a1 1 0 011.213-.727zM9.03 8l-1 4h2.938l1-4H9.031z" clipRule="evenodd" /></svg>
                                Project Mode: {projectContext.name}
                            </span>
                        )}
                    </h2>
                    <p className="text-slate-500">{workflowDetails?.description}</p>
                </div>
                
                {isLoading && <Loader message="Generating analysis..." />}

                {!isLoading && !['crisis_template_builder', 'crisis_management_plan', 'business_resiliency_framework', 'emergency_preparedness_plan', 'local_incident_management_checklist'].includes(activeWorkflow!) && !(result && ['daily_micro_report', 'country_geopolitical_analysis'].includes(activeWorkflow!)) && (
                    <AnalysisForm 
                        suiteName={suiteName}
                        workflowId={activeWorkflow!}
                        workflowName={workflowDetails?.name || activeWorkflow}
                        formData={formData}
                        isLoading={isLoading}
                        error={error}
                        onSubmit={handleSubmit}
                        onFormChange={handleFormChange}
                        onRequestHelp={onRequestHelp}
                        onNavigate={onNavigate}
                        onAutoRunThreatAnalysis={async () => {
                            setIsBackgroundLoading(true);
                            try {
                                const functions = getFunctions();
                                const onDemandAnalysisProxy = httpsCallable(functions, 'onDemandAnalysisProxy');
                                
                                const payload = {
                                    analysisType: 'Security & EP Operations Risk Analysis',
                                    formData: {
                                        ...formData, // Pass all fields including ep_pp_ prefixes
                                        useSearch: true
                                    },
                                };

                                const response: any = await onDemandAnalysisProxy(payload);
                                const threatResult = response.data.result;

                                setFormData(prev => ({
                                    ...prev,
                                    ep_pp_threat_level: threatResult.threatLevel,
                                    ep_pp_threat_analysis_summary: threatResult.executiveSummary,
                                    ep_pp_threat_analysis_data: threatResult
                                }));
                            } catch (e) {
                                console.error("Auto threat analysis failed:", e);
                            } finally {
                                setIsBackgroundLoading(false);
                            }
                        }}
                        onInitiateThreatAnalysis={() => {
                            setSubWorkflowOrigin('project_planner');
                            setSubWorkflowPrefill({ ...formData });
                            setFormData(prev => ({
                                ...prev,
                                principal_name: prev.ep_pp_principal_name,
                                threat_summary: prev.ep_pp_threat_summary,
                                location_address: prev.ep_pp_location_address,
                                location_city: prev.ep_pp_location_city,
                                location_country: prev.ep_pp_location_country,
                            }));
                            setActiveWorkflow('ep_threat_analysis');
                            setResult(null);
                        }}
                        onStartFsatAudit={(planData: FormState) => {
                            setPlanDataForAudit(planData);
                            setActiveWorkflow('facility_risk_assessment');
                        }}
                        planDataForAudit={planDataForAudit}
                        onAuditComplete={() => setPlanDataForAudit(null)}
                        lessonPlans={props.lessonPlans}
                        onSaveLessonPlan={props.onSaveLessonPlan}
                        onUpdateSrmProjectModule={props.onUpdateSrmProjectModule}
                        currentUser={props.currentUser}
                        isBackgroundLoading={isBackgroundLoading}
                        onInitializeOperation={(activeWorkflow === 'project_planner' ? handleInitializeEpOperation : undefined) as any}
                        srmProject={projectContext || undefined}
                        srmModule={currentModule || undefined}
                    />
                )}

                <ApiErrorDisplay error={error} serviceName={workflowDetails?.name || 'Analysis'} />
                
                {result && (
                        <div className="mt-8 space-y-6">
                            {subWorkflowOrigin === 'project_planner' && (
                                <div className="bg-indigo-600 p-4 rounded-xl flex items-center justify-between text-white shadow-lg animate-pulse-subtle">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-indigo-500 rounded-lg">
                                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                                        </div>
                                        <div>
                                            <p className="font-bold">Threat Analysis Complete</p>
                                            <p className="text-xs text-indigo-100 italic">Ready to finalize the EP Project Plan (Stage 5)</p>
                                        </div>
                                    </div>
                                    <button 
                                        onClick={handleFinishSubWorkflow}
                                        className="bg-white text-indigo-600 px-6 py-2 rounded-lg font-bold hover:bg-slate-50 transition-colors shadow-sm"
                                    >
                                        Finish & Continue to Report &rarr;
                                    </button>
                                </div>
                            )}
                            <AnalysisResultDisplay 
                                result={result} 
                                sources={sources}
                                activeWorkflow={activeWorkflow || undefined}
                                onNavigate={onNavigate}
                                customSaveLabel={
                                    projectContext ? "Save to Framework & Return" :
                                    (frameworkContextId ? "Save & Return to Command Center" : undefined)
                                }
                                onConvertAnalysisToProject={handleConvertAnalysisToProject}
                                onStartFsatAudit={(planData: any) => {
                                    setPlanDataForAudit(planData);
                                    setActiveWorkflow('facility_risk_assessment');
                                }}
                                organizationId={props.currentOrganization?.id || projectContext?.organizationId}
                                clientId={projectContext?.clientId}
                                onLaunchChecklist={(checklistName) => {
                                    setLinkedChecklist(checklistName);
                                    setLinkedIncidentType(String((result as any)?.incidentType || ''));
                                    setIsLocalIncidentBuilderOpen(true);
                                }}
                            />
                            {/* Gap 4 — EP Ops Risk Analysis writeback to project record */}
                            {activeWorkflow === 'ep_ops_risk_analysis' && selectedEpProject && (
                                <div className="bg-emerald-600 p-4 rounded-xl flex items-center justify-between text-white shadow-lg">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-emerald-500 rounded-lg">
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                                        </div>
                                        <div>
                                            <p className="font-bold text-sm">Ops Risk Analysis Complete</p>
                                            <p className="text-xs text-emerald-100">Save this summary to the Mission Command Center OPORD annex.</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={async () => {
                                            try {
                                                const summary = (result as any)?.executiveSummary || (result as any)?.overallRiskAssessment || 'Ops Risk Analysis completed.';
                                                const projectRef = doc(db, 'ep_projects', selectedEpProject.id);
                                                await updateDoc(projectRef, { opsRiskSummary: summary });
                                                setSelectedEpProject(prev => prev ? { ...prev, opsRiskSummary: summary } : null);
                                                void showAppAlert('Ops Risk summary saved to Mission Command Center.');
                                            } catch (e) {
                                                console.error('Failed to save Ops Risk summary:', e);
                                            }
                                        }}
                                        className="bg-white text-emerald-700 px-5 py-2 rounded-lg font-bold text-sm hover:bg-emerald-50 transition-colors shadow-sm whitespace-nowrap"
                                    >
                                        Save to Command Center &rarr;
                                    </button>
                                </div>
                            )}
                        </div>
                )}
                
                {workflowDetails && activeWorkflow !== 'facility_risk_assessment' && activeWorkflow !== 'project_planner' && (
                    <WorkflowChecklist workflowId={activeWorkflow} workflowName={workflowDetails.name} />
                )}
            </>
        );
    }


    return (
        <>
            <div className="animate-fade-in">
                {fullWorkspaceAccess && props.currentOrganization?.id && navParams?.serviceRequestId
                    ? <ServiceRecallWorkspace key={navParams.serviceRequestId} organizationId={props.currentOrganization.id}
                        requestId={navParams.serviceRequestId}>{content}</ServiceRecallWorkspace> : content}
            </div>

            {/* ✅ CMP Workflow Modals (Moved outside animation wrapper to prevent layout clipping) */}
            <CrisisTemplateBuilder 
                isOpen={isCrisisTemplateBuilderOpen} 
                onClose={() => setIsCrisisTemplateBuilderOpen(false)} 
                organization={props.currentOrganization!} 
                onRequestHelp={() => onRequestHelp(suiteName, undefined, true)}
                onNavigate={onNavigate}
            />
            <CrisisManagementBuilder 
                isOpen={isCrisisBuilderOpen} 
                onClose={() => setIsCrisisBuilderOpen(false)} 
                organization={props.currentOrganization!} 
                initialData={navParams?.initialData}
                onComplete={(res) => {
                    setIsCrisisBuilderOpen(false);
                    setResult(res);
                    if (!activeWorkflow) setActiveWorkflow('crisis_management_plan');
                }} 
                onRequestHelp={() => onRequestHelp(suiteName, undefined, true)}
                onNavigate={onNavigate as any}
            />
            <BusinessResiliencyBuilder 
                isOpen={isResiliencyBuilderOpen} 
                onClose={() => setIsResiliencyBuilderOpen(false)} 
                organization={props.currentOrganization!} 
                onComplete={(res) => {
                    setIsResiliencyBuilderOpen(false);
                    setResult(res);
                    if (!activeWorkflow) setActiveWorkflow('business_resiliency_framework');
                }} 
                onRequestHelp={() => onRequestHelp(suiteName, undefined, true)}
                onNavigate={onNavigate as any}
            />
            <EmergencyPreparednessBuilder 
                isOpen={isEmergencyBuilderOpen} 
                onClose={() => setIsEmergencyBuilderOpen(false)} 
                organization={props.currentOrganization!} 
                onComplete={(res) => {
                    setIsEmergencyBuilderOpen(false);
                    setResult(res);
                    if (!activeWorkflow) setActiveWorkflow('emergency_preparedness_plan');
                }} 
                onRequestHelp={() => onRequestHelp(suiteName, undefined, true)}
                onNavigate={onNavigate as any}
            />
            <LocalIncidentManagementBuilder 
                isOpen={isLocalIncidentBuilderOpen} 
                onClose={() => {
                    setIsLocalIncidentBuilderOpen(false);
                    setLinkedChecklist(undefined);
                    setLinkedIncidentType(undefined);
                }}
                organization={props.currentOrganization!} 
                onComplete={(res: any) => {
                    setIsLocalIncidentBuilderOpen(false);
                    setLinkedChecklist(undefined);
                    setLinkedIncidentType(undefined);
                    setResult(res);
                    if (!activeWorkflow) setActiveWorkflow('local_incident_management_checklist');
                }} 
                initialChecklist={linkedChecklist || navParams?.initialChecklist}
                initialIncidentType={linkedIncidentType || navParams?.incidentType}
            />
        </>
    );
};

export default SuiteDashboard;
